import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RmsModule } from '@rms';
import { WebService, lastValueFrom, RmsMessageService, PromptService, RmsPulldownService } from '@services';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { CreateTransferComponent } from './create-transfer/create-transfer.component';
import { ContainerDetailsComponent } from './container-details/container-details.component';

// A child ("holding tank") inventory row waiting to be moved to an outbound container.
// Matches what create_inventory_transfer_child inserts and get_holding_tank_inventory returns.
interface HoldingTankItem {
  InventoryID: number;
  ParentInventoryID: number;
  BagsOrdered: number;
  NetWeight: number;
  DestinationID: number;
  DestinationName: string;
  OriginWarehouseID?: number;
  OriginName?: string;        // where this item was transferred from
  InventoryTypeName?: string; // the coffee itself (e.g. "Colombia Nariño Excelso")
  FinalPosition: string;      // 4.6.8
  Marks?: string;
  SupplierReffNumber?: string;
  DateFixed?: string | Date;  // 4.6.7
  TitleDate?: string | Date;  // when title/aging reset for this transfer
  DateIn?: string | Date;     // when this transfer actually happened
  MaxWeight: number;          // 4.6.13 - comes from the API, per destination
  ParentAgedCost?: number;
  ParentAgedDifferential?: number;
  HoldingAgedCost?: number;
  HoldingAgedDifferential?: number;
  TransferFeePerLb?: number;
  ProjectedTransferCost?: number;
  ProjectedTransferDifferential?: number;
}
interface AvailableInventoryOption {
  LabelName: string;
  InventoryID: number;
  InventoryTypeName?: string;
  AvailableQty?: number;
  FinalDestinationWarehouseID?: number | null;
  FinalDestinationWarehouseName?: string | null;
}

interface TransferPulldownResult {
  action: 'save';
  mode: 'create' | 'edit';

  inventoryID: number;
  sourceInventoryID: number;

  originWarehouseID: number | null;
  previousDestinationID: number | null;
  destinationID: number;

  bagCount: number;
  holdingTankID: number | null;
}
// A line item that's been dragged into the outbound container, pending shipment.
interface TransferLineItem extends HoldingTankItem {
  bagsToTransfer: number; // 4.6.9
}

interface TransferOrder {
  ContainerID?: number;
  OriginName?: string;
  DestinationName?: string;
  ToDestinationID: number;
  Status: 'draft' | 'ON_CONTAINER' | 'SHIPPED';
  DeliveryOrderSent?: boolean;
  TotalBags: number;
  TotalWeight: number;
  CreatedDate?: string;      // when this container was created
  ETDDate?: string;
  AfloatDate?: string;
  ETADate?: string;
  WTADate?: string;
  ContainerDeliveredDate?: string;
  TrackingDisplay?: string; // merged "Status — Stage, Date" shown/edited in one column
}

@Component({
  selector: 'app-inventory-transfer',
  standalone: true,
  imports: [CommonModule, FormsModule, RmsModule],
  templateUrl: './inventory-transfer.component.html',
  styleUrl: './inventory-transfer.component.scss'
})
export class InventoryTransferComponent implements OnInit {

  destinations: { DestinationID: number, DestinationName: string, HoldingTankID?: number, MaxWeight?: number }[] = [];
  toDestinationID: number | null = null;
  containerNumber: string = '';
  FromDestinationID: number | null = null; // 4.6.11 - for filtering the holding tank by origin
  availableInventoryOptions: AvailableInventoryOption[] = [];
  warehouseGroup(id: unknown): number { return Number(id) === 43 ? 37 : Number(id); }
  saving = false;
  private holdingTankRequest = 0;
  private loadedDestinationID: number | null = null;
  completingIds = new Set<number>();
  masterWarehouseID: number | null = null;
  trackingOrder: TransferOrder | null = null;
  trackingMilestone = 'ETD';
  trackingDate = '';
  trackingSaving = false;
  get routeReady() { return !!this.FromDestinationID && !!this.toDestinationID && this.warehouseGroup(this.FromDestinationID) !== this.warehouseGroup(this.toDestinationID); }
  get masterWarehouses() { return this.destinations.filter(d => d.HoldingTankID != null).slice(0, 3); }
  async showMasterTank(id: number | null) {
    if (this.saving || this.containerItems.length) { this.message.error('Save or clear the outbound container before changing holding tank views.'); return; }
    this.masterWarehouseID = id;
    await this.loadHoldingTank();
  }
  get holdingPercent() { return Math.min(100, this.currentHoldingTankWeight() / this.weightLimit * 100); }
  get holdingRouteLoads() {
    const routes = new Map<string, {name: string, weight: number, limit: number}>();
    for (const item of this.filteredHoldingTankItems) {
      const key = this.warehouseGroup(item.OriginWarehouseID) + ':' + item.DestinationID;
      const route = routes.get(key) || {name: (this.warehouseGroup(item.OriginWarehouseID) === 37 ? 'NJ' : item.OriginName || 'Unknown origin') + ' → ' + item.DestinationName, weight: 0, limit: Math.min(Number(item.MaxWeight) || 42500, 42500)};
      route.weight += Number(item.NetWeight) * Number(item.BagsOrdered);
      routes.set(key, route);
    }
    return [...routes.values()].map(route => ({...route, percent: Math.min(100, route.weight / route.limit * 100)}));
  }
  private availableOrigins: { DestinationID: number, DestinationName: string }[] = [];

  get originOptions() {
    const origins = new Map<number, string>();
    for (const origin of this.availableOrigins) origins.set(this.warehouseGroup(origin.DestinationID), this.warehouseGroup(origin.DestinationID) === 37 ? 'NJ' : origin.DestinationName);
    for (const item of [...this.holdingTankItems, ...this.containerItems]) {
      if (item.OriginWarehouseID != null) origins.set(this.warehouseGroup(item.OriginWarehouseID), this.warehouseGroup(item.OriginWarehouseID) === 37 ? 'NJ' : item.OriginName ?? 'Unknown origin');
    }
    return Array.from(origins, ([DestinationID, DestinationName]) => ({ DestinationID, DestinationName }));
  }

  get destinationOptions() {
    return this.FromDestinationID ? this.destinations.filter(d => this.warehouseGroup(d.DestinationID) !== this.warehouseGroup(this.FromDestinationID)) : [];
  }

  async loadOrigins() {
    try {
      this.availableOrigins = await lastValueFrom(this.webService.getWebCall('inventory', 'coffee/inventory/transfer/origins'));
    } catch (err) { this.message.error('Could not load transfer origins.'); }
  }

  async onOriginChange(value?: number) {
    if (value !== undefined) {
      if (this.saving || this.containerItems.length) {
        this.FromDestinationID = this.warehouseGroup(this.containerItems[0].OriginWarehouseID) || null;
        return;
      }
      this.masterWarehouseID = null;
      this.FromDestinationID = this.warehouseGroup(value) || null;
      this.toDestinationID = null;
      this.loadedDestinationID = null;
      this.holdingTankItems = [];
      await this.loadHoldingTank();
    }
    this.selectedIds.clear(); this.selectAllChecked = false;
  }

  async onDestinationChange(value: number) {
    if (this.saving || this.containerItems.length) {
      this.toDestinationID = this.loadedDestinationID;
      this.message.error('Save or remove the current container items before changing destination.');
      return;
    }
    if (!this.FromDestinationID) {
      this.toDestinationID = null;
      this.message.error('Select an origin warehouse before choosing a destination.');
      return;
    }
    this.masterWarehouseID = null;
    this.toDestinationID = Number(value) || null;
    if (this.warehouseGroup(this.toDestinationID) === this.warehouseGroup(this.FromDestinationID)) {
      this.toDestinationID = null;
      this.message.error('The destination must differ from the origin.');
      return;
    }
    this.onOriginChange();
    await this.loadHoldingTank();
  }

  holdingTankItems: HoldingTankItem[] = [];
  containerItems: TransferLineItem[] = [];

  selectAllChecked = false; // 4.6.12
  selectedIds = new Set<number>();

  transferOrders: TransferOrder[] = []; // 4.6.10

  loading = false;
  showTransferOrdersView = false;

  toggleTransferOrdersView() {
    this.showTransferOrdersView = !this.showTransferOrdersView;
  }

  constructor(
    private webService: WebService,
    private message: RmsMessageService,
    private prompt: PromptService,
    private pulldownService: RmsPulldownService
  ) { }

  ngOnInit(): void {
    this.loadOrigins();
    this.loadDestinations();
    this.loadTransferOrders();
  }

  async loadTransferOrders() {
    try {
      this.transferOrders = await lastValueFrom(
        this.webService.getWebCall('inventory', 'coffee/inventory/transfer/orders')
      );
      this.recomputeFacilityGroups();
    } catch (err) {
      console.error('Failed to load transfer orders', err);
      this.message.error('Could not load transfer orders.');
    }
  }

  // T10 — groups transfer orders by destination facility for the per-facility table view
  transferOrdersByFacility: { facility: string, orders: TransferOrder[] }[] = [];

  // opens the container's line items in a pulldown overlay, replacing the old expand-row dropdown
  openContainerDetails(row: TransferOrder) {
    const ref = this.pulldownService.open(ContainerDetailsComponent, {
      data: { containerID: row.ContainerID, status: row.Status },
      title: `Container ${row.ContainerID}`,
      maxWidth: '70vw',
      top: '0',
    });
    ref.afterClosed().pipe(take(1)).subscribe(async (result: any) => {
      if (result?.reopened) { await this.loadTransferOrders(); await this.loadHoldingTank(); }
    });
  }

  // T8 — opens the Create Transfer pulldown (same shared overlay component SKU Pricing uses
  // for "View Custom Filters"), passing the already-loaded destinations list along
  async openCreateTransfer(): Promise<void> {
    const inventoryLoaded =
      await this.loadAvailableInventoryOptions();

    if (!inventoryLoaded) {
      return;
    }

    const ref = this.pulldownService.open(
      CreateTransferComponent,
      {
        data: {
          destinations: this.destinations,
          availableInventoryOptions:
            this.availableInventoryOptions
        },
        title: 'Create Transfers',
        width: '94vw',
        maxWidth: '1600px',
        top: '0',
        maxHeight: '100vh',
        minHeight: '30vh'
      }
    );

    ref.afterClosed().pipe(take(1)).subscribe({
      next: (data: TransferPulldownResult | {action:'batch-saved'} | null) => {
        if (data?.action === 'batch-saved') {
          void Promise.all([this.loadHoldingTank(),this.loadOrigins(),this.loadAvailableInventoryOptions()]);
          return;
        }
        if (data?.action !== 'save') {
          return;
        }

        void this.saveTransferFromPulldown(data);
      }
    });
  }
  openEditTransfer(
    inventory: HoldingTankItem
  ): void {
    if (this.saving || this.loading) return;
    const ref = this.pulldownService.open(
      CreateTransferComponent,
      {
        data: {
          destinations: this.destinations,
          availableInventoryOptions:
            this.availableInventoryOptions,
          inventoryToEdit: inventory
        },
        title: `Edit Transfer Ref# ${inventory.InventoryID}`,
        width: '60vw',
        top: '0',
        maxHeight: '100vh',
        minHeight: '30vh'
      }
    );

    ref.afterClosed().pipe(take(1)).subscribe({
      next: (data: TransferPulldownResult | null) => {
        if (data?.action !== 'save') {
          return;
        }

        void this.saveTransferFromPulldown(data);
      }
    });
  }
  private async saveTransferFromPulldown(
    data: TransferPulldownResult
  ): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    const requestBody = {
      InventoryID: data.inventoryID,

      /*
       * Create mode creates a child from the selected inventory.
       * Edit mode updates the existing child inventory.
       */
      createChild: data.mode === 'create',
      Transfer: true,

      overrides: {
        BagsOrdered: data.bagCount,
        BagsInbounded: null,

        FinalDestinationWarehouseID:
          data.destinationID,

        HoldingTankID:
          data.holdingTankID,

        TransferStatus:
          'HOLDING_TANK'
      }
    };

    try {
      await lastValueFrom(
        this.webService.getWebCall(
          'inventory',
          'coffee/inventory/transfer/stage',
          {
            body: requestBody
          }
        )
      );

      const bagWord =
        data.bagCount === 1
          ? 'bag'
          : 'bags';

      if (data.mode === 'edit') {
        this.message.success(
          `Transfer Ref# ${data.inventoryID} updated to ` +
          `${data.bagCount} ${bagWord}.`
        );
      } else {
        this.message.success(
          `${data.bagCount} ${bagWord} moved to the holding tank.`
        );
      }

      await this.refreshAfterTransferSave(data);
    } catch (err: any) {
      console.error(
        'Failed to save inventory transfer',
        {
          error: err,
          requestBody,
          pulldownResult: data
        }
      );

      const apiMessage =
        err?.error?.error ??
        err?.error?.message ??
        err?.message;

      this.message.error(
        apiMessage ??
        (
          data.mode === 'edit'
            ? 'Could not update this transfer.'
            : 'Could not move this ref# to the holding tank.'
        )
      );
    } finally { this.saving = false; }
  }
  private async refreshAfterTransferSave(
    data: TransferPulldownResult
  ): Promise<void> {
    const visibleDestinationWasAffected =
      Number(this.FromDestinationID) === Number(data.originWarehouseID) ||
      Number(this.toDestinationID) ===
      Number(data.destinationID) ||
      Number(this.toDestinationID) ===
      Number(data.previousDestinationID);

    if (visibleDestinationWasAffected) {
      await this.loadHoldingTank();
    }

    /*
     * Refresh this because creating a child changes the source inventory's
     * available quantity.
     */
    await this.loadAvailableInventoryOptions();
    await this.loadOrigins();
  }
  // Merges Status + the furthest-along tracking stage into one clickable cell.
  // Priority: Container Delivered > WTA > ETA > Afloat > ETD.
  private applyTrackingDisplay(order: TransferOrder): TransferOrder {
    const stage =
      (order.ContainerDeliveredDate && { label: 'Container Delivered', date: order.ContainerDeliveredDate }) ||
      (order.WTADate && { label: 'WTA', date: order.WTADate }) ||
      (order.ETADate && { label: 'ETA', date: order.ETADate }) ||
      (order.AfloatDate && { label: order.Status === 'SHIPPED' ? 'Shipped' : 'In transit', date: order.AfloatDate }) ||
      (order.ETDDate && { label: 'ETD', date: order.ETDDate }) ||
      null;

    order.TrackingDisplay = stage
      ? `${stage.label}, ${new Date(stage.date).toLocaleDateString()}`
      : (order.Status === 'SHIPPED' ? 'Shipped' : 'Not started');

    return order;
  }

  private recomputeFacilityGroups() {
    this.transferOrders = this.transferOrders.map(o => this.applyTrackingDisplay(o));
    const groups = new Map<string, TransferOrder[]>();
    for (const order of this.transferOrders) {
      const facility = order.DestinationName ?? 'Unknown Facility';
      if (!groups.has(facility)) groups.set(facility, []);
      groups.get(facility)!.push(order);
    }
    this.transferOrdersByFacility = Array.from(groups.entries())
      .map(([facility, orders]) => ({ facility, orders }))
      .sort((a, b) => a.facility.localeCompare(b.facility));
  }

  async loadDestinations() {
    try {
      this.destinations = await lastValueFrom(
        this.webService.getWebCall('inventory', 'coffee/inventory/transfer/destinations')
      );
    } catch (err) {
      console.error('Failed to load destinations', err);
      this.message.error('Could not load transfer destinations.');
    }
  }

  // 4.6.4 - pulls holding tank items filtered by destination.
  async loadHoldingTank() {
    const request = ++this.holdingTankRequest;
    if (!this.masterWarehouseID && !this.routeReady) {
      this.holdingTankItems = [];
      this.loading = false;
      this.onOriginChange();
      return;
    }
    const destinationID = this.toDestinationID ? Number(this.toDestinationID) : null;
    this.loading = true;
    this.onOriginChange();
    try {
      const items: HoldingTankItem[] = await lastValueFrom(
        this.webService.getWebCall('inventory', 'coffee/inventory/transfer/holding-tank', {
          params: [
            ...(this.masterWarehouseID ? [{ name: 'WarehouseID', value: this.masterWarehouseID }] : [
              { name: 'DestinationID', value: destinationID }, { name: 'OriginID', value: Number(this.FromDestinationID) }])
          ]
        })
      );
      if (request !== this.holdingTankRequest) return;
      this.loadedDestinationID = destinationID;
      this.holdingTankItems = items.filter(item => !this.containerItems.some(line => Number(line.InventoryID) === Number(item.InventoryID)));
    } catch (err) {
      if (request !== this.holdingTankRequest) return;
      this.holdingTankItems = [];
      console.error('Failed to load holding tank', err);
      this.message.error('Could not load the holding tank for this destination.');
    } finally {
      if (request === this.holdingTankRequest) this.loading = false;
    }
  }
  get filteredHoldingTankItems(): HoldingTankItem[] {
    if (this.masterWarehouseID) return this.holdingTankItems.filter(i => this.warehouseGroup(i.OriginWarehouseID) === this.warehouseGroup(this.masterWarehouseID) || this.warehouseGroup(i.DestinationID) === this.warehouseGroup(this.masterWarehouseID));
    if (!this.routeReady) return [];
    return this.holdingTankItems.filter(i => this.warehouseGroup(i.OriginWarehouseID) === this.warehouseGroup(this.FromDestinationID)
      && (!this.toDestinationID || Number(i.DestinationID) === Number(this.toDestinationID)));
  }
  // resolves the currently selected destination's display name for the route banner
  selectedDestinationName(): string {
    if (!this.toDestinationID) return 'a destination';
    return this.destinations.find(d => d.DestinationID === this.toDestinationID)?.DestinationName ?? 'a destination';
  }

  // 4.6.13 - weight limit is per-destination, comes back with the holding tank data. 
  get weightLimit(): number {
    const limit = Number(this.destinations.find(d => Number(d.DestinationID) === Number(this.toDestinationID))?.MaxWeight
      ?? this.holdingTankItems[0]?.MaxWeight ?? this.containerItems[0]?.MaxWeight);
    return limit > 0 && Number.isFinite(limit) ? Math.min(limit, 42500) : 42500;
  }

  // 4.6.12 select all
  canLoadReference(item: HoldingTankItem): boolean {
    return /^INSTORE\b/i.test(String(item.FinalPosition ?? '').trim());
  }
  toggleSelectAll(checked: boolean) {
    this.selectAllChecked = checked;
    this.selectedIds.clear();
    if (checked) {
      this.filteredHoldingTankItems.filter(i => this.canLoadReference(i)).forEach(i => this.selectedIds.add(i.InventoryID));
    }
  }

  toggleSelectItem(id: number, checked: boolean) {
    if (checked && !this.filteredHoldingTankItems.some(i => i.InventoryID === id && this.canLoadReference(i))) return;
    checked ? this.selectedIds.add(id) : this.selectedIds.delete(id);
    this.selectAllChecked = this.filteredHoldingTankItems.length > 0 && this.filteredHoldingTankItems.every(i => this.selectedIds.has(i.InventoryID));
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  moveSelectedToContainer() {
    if (this.saving || this.loading) return;
    const toMove = this.filteredHoldingTankItems.filter(i => this.selectedIds.has(i.InventoryID));
    toMove.forEach(item => this.moveItemToContainer(item));
    this.selectedIds.clear();
    this.selectAllChecked = false;
  }

  // 4.6.4 drag and drop, using the app's existing appDragDrop directive
  onDropToContainer(item: HoldingTankItem) {
    this.moveItemToContainer(item);
  }

  private moveItemToContainer(item: HoldingTankItem) {
    if (this.masterWarehouseID || !this.routeReady || this.saving || this.loading || !item || this.containerItems.some(i => i.InventoryID === item.InventoryID)) return;
    if (!this.canLoadReference(item)) {
      this.message.error('This transfer can stay staged, but its source coffee must be in-store before loading a container.');
      return;
    }
    if (!this.holdingTankItems.some(i => i.InventoryID === item.InventoryID)
      || Number(item.DestinationID) !== Number(this.toDestinationID)) {
      this.message.error('Choose an item from the selected destination holding tank.');
      return;
    }
    if (!this.FromDestinationID || this.warehouseGroup(item.OriginWarehouseID) !== this.warehouseGroup(this.FromDestinationID)
      || (this.containerItems.length && this.warehouseGroup(item.OriginWarehouseID) !== this.warehouseGroup(this.containerItems[0].OriginWarehouseID))) {
      this.message.error('A container must follow one origin-to-destination route. Choose coffee from the selected origin.');
      return;
    }
    if (!Number.isInteger(Number(item.BagsOrdered)) || Number(item.BagsOrdered) <= 0 || !(Number(item.NetWeight) > 0) || !Number.isFinite(Number(item.NetWeight))) {
      this.message.error('This reference needs a valid bag quantity and weight before loading.');
      return;
    }
    const lineItem: TransferLineItem = { ...item, bagsToTransfer: Number(item.BagsOrdered) };
    const projectedWeight = this.currentContainerWeight() + (lineItem.NetWeight * lineItem.bagsToTransfer);
    const limit = this.weightLimit;

    if (projectedWeight > limit) {
      // 4.6.5
      this.message.error(`Adding ref# ${item.InventoryID} would exceed the ${limit.toLocaleString()} lb limit for ${item.DestinationName}.`);
      return;
    }

    this.containerItems.push(lineItem);
    this.holdingTankItems = this.holdingTankItems.filter(i => i.InventoryID !== item.InventoryID);
    this.selectedIds.delete(item.InventoryID);
    this.selectAllChecked = false;
  }

  removeFromContainer(id: number) {
    if (this.saving) return;
    const item = this.containerItems.find(i => i.InventoryID === id);
    this.containerItems = this.containerItems.filter(i => i.InventoryID !== id);
    if (item) {
      const { bagsToTransfer, ...holdingItem } = item;
      this.holdingTankItems = [...this.holdingTankItems, holdingItem as HoldingTankItem];
    }
  }

  // 4.6.9 adjust quantity until shipped
  updateBagQuantity(item: TransferLineItem, newBagCount: number) {
    if (this.saving) return;
    newBagCount = Number(newBagCount);
    if (!Number.isInteger(newBagCount) || newBagCount <= 0 || newBagCount > item.BagsOrdered) {
      this.message.error(`Enter a value between 1 and ${item.BagsOrdered} bags.`);
      return;
    }
    const otherWeight = this.currentContainerWeight() - (item.NetWeight * item.bagsToTransfer);
    if (otherWeight + (item.NetWeight * newBagCount) > this.weightLimit) {
      this.message.error(`That quantity would exceed the ${this.weightLimit.toLocaleString()} lb limit.`);
      return;
    }
    item.bagsToTransfer = newBagCount;
  }

  currentContainerWeight(): number {
    return this.containerItems.reduce((sum, i) => sum + (i.NetWeight * i.bagsToTransfer), 0);
  }

  currentContainerBags(): number {
    return this.containerItems.reduce((sum, i) => sum + i.bagsToTransfer, 0);
  }

  get overLimit(): boolean {
    return this.currentContainerWeight() > this.weightLimit;
  }
  get loadPercent(): number { return Math.min(100, this.currentContainerWeight() / this.weightLimit * 100); }

  // T7 — same idea as currentContainerWeight/Bags, but for what's sitting in the holding tank
  currentHoldingTankWeight(): number {
    return this.filteredHoldingTankItems.reduce((sum, i) => sum + (i.NetWeight * i.BagsOrdered), 0);
  }

  currentHoldingTankBags(): number {
    return this.filteredHoldingTankItems.reduce((sum, i) => sum + Number(i.BagsOrdered), 0);
  }

  get holdingTankOverLimit(): boolean {
    return this.currentHoldingTankWeight() > this.weightLimit;
  }

  async saveDraft() {
    if (this.saving || this.loading) return;
    if (!this.containerItems.length || !this.toDestinationID) {
      this.message.error('Add at least one item before saving.');
      return;
    }
    if (this.overLimit || this.containerItems.some(i => Number(i.DestinationID) !== Number(this.toDestinationID)
      || !this.FromDestinationID || this.warehouseGroup(i.OriginWarehouseID) !== this.warehouseGroup(this.FromDestinationID)
      || !Number.isInteger(i.bagsToTransfer) || i.bagsToTransfer <= 0 || i.bagsToTransfer > i.BagsOrdered)) {
      this.message.error('Check the destination, whole bag quantities, and load weight before saving.');
      return;
    }

    const body = {
      ToDestinationID: this.toDestinationID,
      LineItems: this.containerItems.map(i => ({ InventoryID: i.InventoryID, BagsToTransfer: i.bagsToTransfer }))
    };

    this.saving = true;
    try {
      const saved: any = await lastValueFrom(
        this.webService.getWebCall('inventory', 'coffee/inventory/transfer/save', { body })
      );
      const destinationName = this.destinations.find(d => d.DestinationID === saved.ToDestinationID)?.DestinationName;
      this.transferOrders = [{
        ContainerID: saved.ContainerID,
        DestinationName: destinationName,
        OriginName: this.containerItems[0]?.OriginName,
        ToDestinationID: saved.ToDestinationID,
        Status: saved.Status,
        CreatedDate: saved.CreatedDate,
        TotalBags: this.currentContainerBags(),
        TotalWeight: this.currentContainerWeight()
      }, ...this.transferOrders];
      this.recomputeFacilityGroups();
      this.containerItems = [];
      this.message.success(`Transfer order saved — Container ID ${saved.ContainerID}.`);
      await this.loadHoldingTank();
    } catch (err: any) {
      console.error('Failed to save transfer order', err);
      this.message.error(err?.error?.error ?? 'Could not save the transfer order.');
    } finally {
      this.saving = false;
    }
  }

  // 4.6.6 - trader completes the transfer; DO is sent to outbound automatically.
  async completeTransferAndSendDO(order: TransferOrder) {
    // Delivery-order dispatch belongs to Outbound. No send/resend action runs here.
    return;
  }

  readonly trackingStatusOptions = [
    { label: 'Estimated departure (ETD)', value: 'ETD', field: 'ETDDate' },
    { label: 'Shipped / departed', value: 'Shipped', field: 'AfloatDate' },
    { label: 'In transit', value: 'Afloat', field: 'AfloatDate' },
    { label: 'Estimated arrival (ETA)', value: 'ETA', field: 'ETADate' },
    { label: 'Warehouse arrival (WTA)', value: 'WTA', field: 'WTADate' },
    { label: 'Delivered', value: 'Container Delivered', field: 'ContainerDeliveredDate' }
  ];
  openTrackingDialog(order: TransferOrder, milestone = 'ETD') {
    if (this.trackingSaving) return;
    this.trackingOrder = order;
    this.trackingMilestone = milestone;
    this.onTrackingMilestoneChange();
  }
  onTrackingMilestoneChange() {
    const field = this.trackingStatusOptions.find(o => o.value === this.trackingMilestone)?.field;
    this.trackingDate = field ? String((this.trackingOrder as any)?.[field] || '').slice(0, 10) : '';
  }
  async saveTracking() {
    const order = this.trackingOrder;
    if (!order?.ContainerID || !this.trackingDate || this.trackingSaving) return;
    this.trackingSaving = true;
    try {
      const shipped = this.trackingMilestone === 'Shipped';
      const updated: any = await lastValueFrom(this.webService.getWebCall('inventory',
        shipped ? 'coffee/inventory/transfer/shipped' : 'coffee/inventory/transfer/update-tracking',
        {body: {ContainerID: order.ContainerID, TrackingStatus: this.trackingMilestone, TrackingDate: this.trackingDate}}));
      if (!updated?.ContainerID) throw new Error('Tracking update was not confirmed.');
      Object.assign(order, updated);
      this.applyTrackingDisplay(order);
      this.trackingOrder = null;
      this.message.success(shipped ? 'Container marked shipped.' : 'Tracking updated.');
    } catch (err: any) { this.message.error(err?.error?.error || 'Could not save tracking.'); }
    finally { this.trackingSaving = false; }
  }
  async loadAvailableInventoryOptions(): Promise<boolean> {
    try {
      const result = await lastValueFrom(
        this.webService.getWebCall(
          'inventory',
          'coffee/inventory/available-for-transfer'
        )
      );

      this.availableInventoryOptions =
        Array.isArray(result)
          ? result.map((item: any) => ({
            ...item,
            InventoryID: Number(item.InventoryID),

            AvailableQty:
              item.AvailableQty == null
                ? undefined
                : Number(item.AvailableQty),

            FinalDestinationWarehouseID:
              item.FinalDestinationWarehouseID == null
                ? null
                : Number(
                  item.FinalDestinationWarehouseID
                )
          }))
          : [];

      return true;
    } catch (err) {
      console.error(
        'Failed to load available inventory',
        err
      );

      this.availableInventoryOptions = [];

      this.message.error(
        'Could not load available inventory.'
      );

      return false;
    }
  }
}
